/**
 * 移动端侧边抽屉菜单
 */

import { useEffect, useState } from 'react'
import { X, User, Bot, MessageCircle, Bookmark, Wallet, Settings, LogOut, HelpCircle, CircleDot, Crown, Sun, Moon, Download, Check, Heart, LayoutDashboard } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useLanguageStore } from '@/stores/languageStore'
import { useAuth } from '@/modules/shared/contexts/AuthContext'
import { useTheme } from '@/modules/shared/hooks/useTheme'
import { usePWAInstall } from '@/modules/shared/hooks/usePWAInstall'
import { THEME_PRESETS, DEFAULT_THEME_ID, syncThemeColorMeta } from '@/modules/shared/styles/themePresets'
import { useT } from '@/i18n'
import { KommendLogo } from '@/modules/shared/components/KommendLogo'

interface MenuItem {
  id: string
  icon: typeof User
  labelKey: string
  href?: (lang: string) => string
  requireAuth?: boolean
}

const MENU_ITEMS: MenuItem[] = [
  { id: 'profile', icon: User, labelKey: 'navigation.drawer.myProfile', requireAuth: true },
  { id: 'myagent', icon: Bot, labelKey: 'navigation.drawer.myAgent', requireAuth: true },
  { id: 'messages', icon: MessageCircle, labelKey: 'navigation.main.messages', requireAuth: true },
  { id: 'circles', icon: CircleDot, labelKey: 'navigation.main.circles' },
  { id: 'myCircles', icon: CircleDot, labelKey: 'circles.my.pageTitle', href: (lang) => `/${lang}/me/circles`, requireAuth: true },
  { id: 'bookmarks', icon: Bookmark, labelKey: 'navigation.drawer.bookmarks', requireAuth: true },
  { id: 'membership', icon: Crown, labelKey: 'navigation.drawer.membership', requireAuth: true },
  { id: 'account', icon: Wallet, labelKey: 'navigation.account', href: (lang) => `/${lang}/account`, requireAuth: true },
  { id: 'studio', icon: LayoutDashboard, labelKey: 'creatorSubscription.studioShell.title', href: (lang) => `/${lang}/studio`, requireAuth: true },
  { id: 'mySubscriptions', icon: Heart, labelKey: 'creatorSubscription.fan.mySubscriptions', href: (lang) => `/${lang}/me/subscriptions`, requireAuth: true },
  { id: 'settings', icon: Settings, labelKey: 'navigation.userMenu.settings' },
  { id: 'help', icon: HelpCircle, labelKey: 'navigation.userMenu.help' },
]

interface MobileDrawerProps {
  isOpen: boolean
  onClose: () => void
  onNavigate?: (page: string) => void
}

export function MobileDrawer({ isOpen, onClose, onNavigate }: MobileDrawerProps) {
  const t = useT()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { language } = useLanguageStore()
  const { toggleTheme, isDark } = useTheme()
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall()
  const [currentThemeId, setCurrentThemeId] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME_ID
    return localStorage.getItem('y-theme-preset') ?? DEFAULT_THEME_ID
  })

  // 打开时禁止背景滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // ESC 关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleEsc)
    }
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  const handleLogout = async () => {
    await signOut()
    onClose()
    navigate('/')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100]">
      {/* 背景遮罩 */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 抽屉面板 */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-[280px] bg-[var(--y-surface-primary)] border-r border-[var(--y-border)] flex flex-col animate-slide-in-left"
      >
        {/* 头部 */}
        <div className="p-4 border-b border-[var(--y-border)]">
          <div className="flex items-center justify-between mb-4">
            <KommendLogo size={32} />
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-[var(--y-surface-secondary)] transition-colors"
              aria-label={t('common.action.close')}
            >
              <X className="w-5 h-5 text-[var(--y-text-tertiary)]" />
            </button>
          </div>

          {/* 用户信息 */}
          {user ? (
            <div 
              className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-[var(--y-surface-secondary)]/50 cursor-pointer transition-colors"
              onClick={() => {
                onNavigate?.('profile')
              }}
            >
              <img
                src={user.avatar_url || '/logo/y-logo/y3.webp'}
                alt="Avatar"
                className="w-10 h-10 rounded-full object-cover ring-2 ring-[var(--y-border)]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[var(--y-text-primary)] font-medium truncate">
                  {user.display_name || user.username || 'User'}
                </p>
                <p className="text-[var(--y-text-tertiary)] text-sm truncate">
                  @{user.username || 'user'}
                </p>
              </div>
            </div>
          ) : (
            <button
              onClick={() => onNavigate?.('login')}
              className="w-full py-2.5 px-4 bg-[var(--y-accent)] text-white font-medium rounded-full hover:bg-[var(--y-accent-hover)] transition-colors"
            >
              {t('navigation.userMenu.loginSignup')}
            </button>
          )}
        </div>

        {/* 菜单列表 */}
        <nav className="flex-1 overflow-y-auto py-2">
          {MENU_ITEMS.map(({ id, icon: Icon, labelKey, href, requireAuth }) => {
            // 需要登录但未登录时隐藏
            if (requireAuth && !user) return null

            return (
              <button
                key={id}
                onClick={() => {
                  if (href) {
                    navigate(href(language))
                    onClose()
                    return
                  }
                  onNavigate?.(id)
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--y-surface-secondary)]/50 active:bg-[var(--y-surface-secondary)] transition-colors"
              >
                <Icon className="w-5 h-5 text-[var(--y-text-tertiary)]" />
                <span className="text-[var(--y-text-primary)]">{t(labelKey)}</span>
              </button>
            )
          })}
        </nav>

        {/* 底部：色系 + 安装 + 主题切换 + 退出登录 */}
        <div className="border-t border-[var(--y-border)]">
          {/* Theme color picker */}
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--y-text-tertiary)] mb-2">{t('navigation.appearance.themeColor')}</p>
            <div className="flex items-center gap-3">
              {THEME_PRESETS.map((theme) => {
                const isActive = theme.id === currentThemeId
                return (
                  <button
                    key={theme.id}
                    onClick={() => {
                      setCurrentThemeId(theme.id)
                      if (theme.id === DEFAULT_THEME_ID) {
                        delete document.documentElement.dataset.theme
                      } else {
                        document.documentElement.dataset.theme = theme.id
                      }
                      localStorage.setItem('y-theme-preset', theme.id)
                      syncThemeColorMeta(theme.id)
                    }}
                    className="relative w-7 h-7 rounded-full transition-transform active:scale-90"
                    style={{
                      backgroundColor: theme.displayColor,
                      boxShadow: isActive ? `0 0 0 2px var(--y-surface-primary), 0 0 0 4px ${theme.displayColor}` : 'none',
                    }}
                    aria-label={theme.name}
                    aria-pressed={isActive}
                  >
                    {isActive && (
                      <Check className="w-3.5 h-3.5 text-white absolute inset-0 m-auto" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.3))' }} />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {canInstall && !isInstalled && (
            isIOS ? (
              <div className="px-4 py-3">
                <div className="flex items-center gap-3 mb-1.5">
                  <Download className="w-5 h-5 text-[var(--y-accent)]" />
                  <span className="text-[var(--y-text-primary)]">{t('navigation.appearance.installApp')}</span>
                </div>
                <p className="text-xs text-[var(--y-text-tertiary)] leading-relaxed pl-8">
                  {t('navigation.appearance.iosInstallGuide')}
                </p>
              </div>
            ) : (
              <button
                onClick={() => { promptInstall(); onClose() }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--y-surface-secondary)]/50 active:bg-[var(--y-surface-secondary)] transition-colors"
              >
                <Download className="w-5 h-5 text-[var(--y-accent)]" />
                <span className="text-[var(--y-text-primary)]">{t('navigation.appearance.installApp')}</span>
              </button>
            )
          )}

          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--y-surface-secondary)]/50 active:bg-[var(--y-surface-secondary)] transition-colors"
          >
            {isDark ? (
              <Sun className="w-5 h-5 text-[var(--y-text-tertiary)]" />
            ) : (
              <Moon className="w-5 h-5 text-[var(--y-text-tertiary)]" />
            )}
            <span className="text-[var(--y-text-primary)]">{isDark ? t('navigation.appearance.lightMode') : t('navigation.appearance.darkMode')}</span>
          </button>

          {user && (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2.5 mb-2 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('navigation.userMenu.logout')}</span>
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-in-left {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.25s ease-out;
        }
      `}</style>
    </div>
  )
}
